import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTasks, createTask, updateTask, deleteTask } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

const PRIORITY_COLORS = {
  low: 'bg-gray-800 text-gray-400',
  medium: 'bg-blue-900/50 text-blue-300',
  high: 'bg-orange-900/50 text-orange-300',
  urgent: 'bg-red-900/50 text-red-300',
};

const STATUS_COLORS = {
  pending: 'bg-gray-800 text-gray-400',
  in_progress: 'bg-blue-900/50 text-blue-300',
  completed: 'bg-green-900/50 text-green-300',
  overdue: 'bg-red-900/50 text-red-300',
};

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-md">{children}</div>
    </div>
  );
}

export default function TasksPage() {
  const { user, isFellow } = useAuth();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', due_date: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', statusFilter, priorityFilter],
    queryFn: () => getTasks({ status: statusFilter || undefined, priority: priorityFilter || undefined }),
  });

  const createMut = useMutation({
    mutationFn: createTask,
    onSuccess: () => { qc.invalidateQueries(['tasks']); toast.success('Task created!'); setShowModal(false); setForm({ title: '', description: '', priority: 'medium', due_date: '' }); },
    onError: () => toast.error('Failed to create task'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateTask(id, data),
    onSuccess: () => { qc.invalidateQueries(['tasks']); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteTask,
    onSuccess: () => { qc.invalidateQueries(['tasks']); toast.success('Task removed'); },
  });

  const tasks = data?.data || [];
  const pending = tasks.filter(t => t.status !== 'completed');
  const done = tasks.filter(t => t.status === 'completed');

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Tasks & Deadlines</h1>
          <p className="text-gray-500 text-sm">{pending.length} active · {done.length} completed</p>
        </div>
        {isFellow && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> New Task
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {['', 'pending', 'in_progress', 'completed', 'overdue'].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${statusFilter === s ? 'bg-primary-600 text-white' : 'bg-surface-100 text-gray-400 hover:text-white'}`}>
            {s === '' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
        <div className="border-l border-gray-800 mx-1" />
        {['', 'urgent', 'high', 'medium', 'low'].map((p) => (
          <button key={p} onClick={() => setPriorityFilter(p)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${priorityFilter === p ? 'bg-primary-600 text-white' : 'bg-surface-100 text-gray-400 hover:text-white'}`}>
            {p === '' ? 'All Priority' : p}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : tasks.length === 0 ? (
        <div className="card text-center py-12">
          <CheckCircleIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No tasks found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className={`card flex items-start gap-4 ${t.status === 'completed' ? 'opacity-60' : ''}`}>
              <button
                onClick={() => updateMut.mutate({ id: t.id, data: { status: t.status === 'completed' ? 'pending' : 'completed' } })}
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${t.status === 'completed' ? 'border-green-500 bg-green-500' : 'border-gray-600 hover:border-green-400'}`}
              >
                {t.status === 'completed' && <CheckCircleIcon className="w-4 h-4 text-white" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium text-sm ${t.status === 'completed' ? 'line-through text-gray-500' : 'text-white'}`}>{t.title}</span>
                  <span className={`badge ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                  <span className={`badge ${STATUS_COLORS[t.status]}`}>{t.status.replace('_', ' ')}</span>
                </div>
                {t.description && <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>}
                <div className="text-xs text-gray-600 mt-1">
                  {t.assigned_to?.full_name}
                  {t.due_date && <span className={`ml-2 ${new Date(t.due_date) < new Date() && t.status !== 'completed' ? 'text-red-400' : ''}`}>
                    · Due {format(new Date(t.due_date), 'dd MMM yyyy')}
                  </span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {t.status === 'pending' && (
                  <button onClick={() => updateMut.mutate({ id: t.id, data: { status: 'in_progress' } })}
                    className="text-xs text-blue-400 hover:text-blue-300 bg-blue-900/20 px-2 py-1 rounded">
                    Start
                  </button>
                )}
                <button onClick={() => { if (window.confirm('Delete task?')) deleteMut.mutate(t.id); }} className="text-red-400 hover:text-red-300 p-1">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Create Task</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              createMut.mutate({ ...form, due_date: form.due_date ? new Date(form.due_date).toISOString() : undefined });
            }} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Description</label>
                <textarea className="input resize-none" rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 block mb-1">Priority</label>
                  <select className="input" value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select></div>
                <div><label className="text-xs text-gray-500 block mb-1">Due Date</label>
                  <input type="date" className="input" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>Create</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
